import React from 'react';
import {
  FaultItemDisplay,
  FaultItemEdit,
  FaultItemError,
  FaultItemLoading,
} from './FaultItem';
import useFaultCause from '../../data/fault/useFaultCause';

interface FaultCauseItemProps {
  index: number;
  causeId: string;
}

interface FaultCauseEditProps extends FaultCauseItemProps {
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

export function FaultCauseEdit({
  causeId,
  index,
  onMoveUp,
  onMoveDown,
  onRemove,
}: FaultCauseEditProps) {
  const { faultCause } = useFaultCause(causeId);

  if (faultCause.isLoading) {
    return <FaultItemLoading />;
  }

  if (!faultCause.data || faultCause.data.length === 0) {
    return <FaultItemError itemType="Cause" id={causeId} />;
  }

  const cause = faultCause.data[0];

  return (
    <FaultItemEdit
      title={cause.title}
      description={cause.description}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onRemove={onRemove}
      index={index}
    />
  );
}

export function FaultCauseItem({ causeId, index }: FaultCauseItemProps) {
  const { faultCause } = useFaultCause(causeId);

  if (faultCause.isLoading) {
    return <FaultItemLoading />;
  }

  if (!faultCause.data || !faultCause.data.length) {
    return <FaultItemError itemType="Cause" id={causeId} />;
  }

  return (
    <FaultItemDisplay
      index={index}
      title={faultCause.data?.[0].title}
      description={faultCause.data?.[0].description}
    />
  );
}

interface FaultCausesProps {
  faultCauseIds: string[];
  renderItem?: (props: FaultCauseItemProps, index: number) => React.ReactNode;
}

const defaultRenderItem = (props: FaultCauseItemProps) => (
  <FaultCauseItem {...props} />
);

export function FaultCauses({
  faultCauseIds,
  renderItem = defaultRenderItem,
}: FaultCausesProps) {
  return faultCauseIds.length === 0 ? (
    <p className="text-sm text-gray-500 dark:text-gray-400">
      No causes defined for this fault.
    </p>
  ) : (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
      {faultCauseIds.map((causeId, index) => (
        <div key={causeId}>
          {renderItem({ causeId, index: index + 1 }, index)}
        </div>
      ))}
    </div>
  );
}
